import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Section, SectionHeader, RowGroup, ToggleRow, RadioRow, SaveButton } from "./_helpers";
import { Image as ImageIcon, Hash, ArrowUpDown, Languages, Stamp, Compass } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const PER_PAGE_OPTS = [12, 24, 30, 60, 100];
const NEW_ACC_OPTS = [0, 7, 14, 30];

export default function InnholdPage() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [dirtyWords, setDirtyWords] = useState(null);
  const [wmDefault, setWmDefault] = useState("off");
  const [wmText, setWmText] = useState("");
  const [wmPreviewKey, setWmPreviewKey] = useState(0);
  const [wmPreviewVariant, setWmPreviewVariant] = useState(null);

  const WATERMARK_OPTS = [
    { v: "off",      label: t("innhold.wm_off") },
    { v: "corner",   label: t("innhold.wm_corner") },
    { v: "diagonal", label: t("innhold.wm_diagonal") },
  ];
  const SORT_GAL = [
    { v: "newest", label: t("innhold.sort_newest") },
    { v: "popular", label: t("innhold.sort_popular") },
    { v: "rating", label: t("innhold.sort_rating") },
  ];
  const SORT_FORUM = [
    { v: "activity", label: t("innhold.sort_activity") },
    { v: "new", label: t("innhold.sort_new") },
    { v: "popular", label: t("innhold.sort_popular") },
  ];
  const VIEW_MODES = [
    { v: "grid", label: t("innhold.view_grid") },
    { v: "list", label: t("innhold.view_list") },
    { v: "large", label: t("innhold.view_large") },
  ];
  const LANG_OPTS = [
    { v: "Bokmål",  label: t("innhold.lang_bokmal") },
    { v: "Nynorsk", label: t("innhold.lang_nynorsk") },
    { v: "Engelsk", label: t("innhold.lang_english") },
  ];

  useEffect(() => {
    api.get("/me/content-prefs-extended").then(r=>setData(r.data)).catch(()=>{});
  }, []);

  useEffect(() => {
    if (user) {
      setWmDefault(user.watermark_default || "off");
      setWmText(user.watermark_text || "");
    }
  }, [user]);

  const saveWatermark = async (patch) => {
    try {
      await api.patch("/users/me", patch);
      toast.success(t("innhold.wm_saved"));
      refresh && refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const update = async (patch) => {
    const next = { ...data, ...patch };
    setData(next);
    try { await api.patch("/me/content-prefs-extended", patch); }
    catch (e) {
      toast.error(formatApiError(e));
      api.get("/me/content-prefs-extended").then(r=>setData(r.data));
    }
  };

  const saveTags = async (key, value) => {
    const tags = value.split(",").map(t=>t.trim()).filter(Boolean);
    try { await api.patch("/me/content-prefs-extended", { [key]: tags }); setData({...data, [key]: tags}); toast.success(t("innhold.saved")); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveWords = async () => {
    const words = (dirtyWords||"").split(",").map(w=>w.trim()).filter(Boolean);
    try { await api.patch("/me/content-prefs-extended", { blocked_words: words }); setData({...data, blocked_words: words}); setDirtyWords(null); toast.success(t("innhold.saved")); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleLang = (l) => {
    const cur = data.preferred_languages || [];
    update({ preferred_languages: cur.includes(l) ? cur.filter(x=>x!==l) : [...cur, l] });
  };

  if (!data) return <div className="font-meta text-[var(--cream-soft)]">{t("innhold.loading")}</div>;

  return (
    <>
      {/* VANNMERKE */}
      <Section testId="watermark-section">
        <SectionHeader icon={Stamp} title={t("innhold.wm_title")} desc={t("innhold.wm_desc")} />
        <RowGroup>
          {WATERMARK_OPTS.map((o) => (
            <RadioRow key={o.v} name="watermark_default" value={o.v} current={wmDefault}
              label={o.label}
              onChange={(v)=>{ setWmDefault(v); saveWatermark({ watermark_default: v }); }}
              testId={`watermark-${o.v}`} />
          ))}
        </RowGroup>
        <div className="mt-5 max-w-md">
          <label className="font-manrope text-sm text-[var(--cream)] block mb-2">
            {t("innhold.wm_text_label")} <span className="font-meta text-[var(--cream-faint)]">{t("innhold.wm_max")}</span>
          </label>
          <input
            value={wmText}
            onChange={(e)=>setWmText(e.target.value)}
            onBlur={()=>{ if ((user?.watermark_text || "") !== wmText) saveWatermark({ watermark_text: wmText }); }}
            placeholder={t("innhold.wm_placeholder", { u: user?.username || t("innhold.wm_username") })}
            maxLength={80}
            className="input-editorial w-full"
            data-testid="watermark-text-input"
          />
          <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-2">
            {t("innhold.wm_help")}
          </div>
        </div>

        {/* FORHÅNDSVISNING */}
        <div className="mt-6">
          <div className="flex flex-wrap gap-2 items-center mb-3">
            <span className="font-meta text-[var(--cream-soft)] mr-2">{t("innhold.wm_preview")}</span>
            {WATERMARK_OPTS.filter(o => o.v !== "off").map((o) => (
              <button
                key={o.v}
                onClick={() => { setWmPreviewVariant(o.v); setWmPreviewKey(k => k + 1); }}
                className={`px-3 py-1.5 text-xs font-meta border transition ${
                  wmPreviewVariant === o.v
                    ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10"
                    : "border-white/15 text-[var(--cream-soft)] hover:border-[var(--magenta)]/60 hover:text-[var(--cream)]"
                }`}
                data-testid={`watermark-preview-${o.v}`}
              >
                {o.v === "corner" ? t("innhold.wm_test_subtle") : t("innhold.wm_test_diagonal")}
              </button>
            ))}
            {wmPreviewVariant && (
              <button
                onClick={() => setWmPreviewVariant(null)}
                className="px-3 py-1.5 text-xs font-meta border border-white/10 text-[var(--cream-faint)] hover:text-[var(--cream)]"
                data-testid="watermark-preview-close"
              >
                {t("innhold.wm_close")}
              </button>
            )}
          </div>
          {wmPreviewVariant && (
            <div className="max-w-lg" data-testid="watermark-preview-image">
              <img
                key={wmPreviewKey}
                src={`${process.env.REACT_APP_BACKEND_URL}/api/me/watermark-preview?variant=${wmPreviewVariant}&text=${encodeURIComponent(wmText || "")}&_=${wmPreviewKey}`}
                alt={t("innhold.wm_preview_alt")}
                className="w-full border border-white/10"
              />
              <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-2">
                {t("innhold.wm_preview_help")}
              </div>
            </div>
          )}
        </div>
      </Section>

      {/* EROTISK */}
      <Section testId="erotic-section">
        <SectionHeader icon={ImageIcon} title={t("innhold.erotic_title")} desc={t("innhold.erotic_desc")} />
        <RowGroup>
          <ToggleRow testId="pref-show-erotic" label={t("innhold.show_erotic")}
            sub={t("innhold.show_erotic_sub")}
            checked={data.show_erotic} onChange={(v)=>update({show_erotic:v})} />
          <ToggleRow testId="pref-jobbsafe" label={t("innhold.jobbsafe")}
            sub={t("innhold.jobbsafe_sub")}
            checked={data.jobbsafe_blur} disabled={!data.show_erotic}
            onChange={(v)=>update({jobbsafe_blur:v})} />
        </RowGroup>
      </Section>

      {/* TAGS */}
      <Section testId="tags-section">
        <SectionHeader icon={Hash} title={t("innhold.tags_title")} desc={t("innhold.tags_desc")} />
        <div className="space-y-5 max-w-md">
          <div>
            <label className="font-manrope text-sm text-[var(--cream)] block mb-2">
              {t("innhold.tags_block")} <span className="font-meta text-[var(--cream-faint)]">{t("innhold.tags_block_hint")}</span>
            </label>
            <textarea defaultValue={(data.blocked_tags || []).join(", ")}
              onBlur={(e)=>{ if(e.target.value !== (data.blocked_tags||[]).join(", ")) saveTags("blocked_tags", e.target.value); }}
              rows={2} placeholder={t("innhold.tags_block_ph")}
              className="input-editorial w-full" data-testid="blocked-tags-input" />
          </div>
          <div>
            <label className="font-manrope text-sm text-[var(--cream)] block mb-2">
              {t("innhold.tags_pref")} <span className="font-meta text-[var(--cream-faint)]">{t("innhold.tags_pref_hint")}</span>
            </label>
            <textarea defaultValue={(data.preferred_tags || []).join(", ")}
              onBlur={(e)=>{ if(e.target.value !== (data.preferred_tags||[]).join(", ")) saveTags("preferred_tags", e.target.value); }}
              rows={2} placeholder={t("innhold.tags_pref_ph")}
              className="input-editorial w-full" data-testid="preferred-tags-input" />
          </div>
        </div>
      </Section>

      {/* SORTERING */}
      <Section testId="sort-section">
        <SectionHeader icon={ArrowUpDown} title={t("innhold.sort_title")} />
        <div className="space-y-6">
          <div>
            <h3 className="font-manrope text-base text-[var(--cream)] mb-3">{t("innhold.sort_gallery")}</h3>
            <RowGroup>
              {SORT_GAL.map((o) => (
                <RadioRow key={o.v} name="sort_gallery" value={o.v} current={data.default_sort_gallery}
                  label={o.label} testId={`sort-gal-${o.v}`} onChange={(v)=>update({default_sort_gallery:v})} />
              ))}
            </RowGroup>
          </div>
          <div>
            <h3 className="font-manrope text-base text-[var(--cream)] mb-3">{t("innhold.sort_stories")}</h3>
            <RowGroup>
              {SORT_GAL.map((o) => (
                <RadioRow key={o.v} name="sort_noveller" value={o.v} current={data.default_sort_noveller}
                  label={o.label} testId={`sort-nov-${o.v}`} onChange={(v)=>update({default_sort_noveller:v})} />
              ))}
            </RowGroup>
          </div>
          <div>
            <h3 className="font-manrope text-base text-[var(--cream)] mb-3">{t("innhold.sort_forum")}</h3>
            <RowGroup>
              {SORT_FORUM.map((o) => (
                <RadioRow key={o.v} name="sort_forum" value={o.v} current={data.default_sort_forum}
                  label={o.label} testId={`sort-forum-${o.v}`} onChange={(v)=>update({default_sort_forum:v})} />
              ))}
            </RowGroup>
          </div>
        </div>
      </Section>

      {/* FILTERS */}
      <Section testId="content-filters-section">
        <SectionHeader title={t("innhold.filters_title")} />
        <RowGroup>
          <ToggleRow testId="autoplay-videos" label={t("innhold.autoplay")}
            sub={t("innhold.autoplay_sub")}
            checked={data.autoplay_videos} onChange={(v)=>update({autoplay_videos:v})} />
          <ToggleRow testId="only-verified-creators" label={t("innhold.only_creators")}
            sub={t("innhold.only_creators_sub")}
            checked={data.only_verified_creators} onChange={(v)=>update({only_verified_creators:v})} />
          <ToggleRow testId="only-network" label={t("innhold.only_network")}
            sub={t("innhold.only_network_sub")}
            checked={data.only_my_network} onChange={(v)=>update({only_my_network:v})} />
          <ToggleRow testId="hide-no-avatar" label={t("innhold.hide_no_avatar")}
            checked={data.hide_no_avatar} onChange={(v)=>update({hide_no_avatar:v})} />
        </RowGroup>
        <div className="mt-5">
          <label className="font-manrope text-sm text-[var(--cream)] block mb-2">{t("innhold.hide_young")}</label>
          <div className="flex gap-2 flex-wrap" data-testid="hide-young-options">
            {NEW_ACC_OPTS.map((d) => (
              <button key={d} onClick={()=>update({hide_account_younger_than_days:d})}
                className={`px-3 py-1 text-sm border ${data.hide_account_younger_than_days === d ? "border-[var(--magenta)] text-[var(--magenta)]" : "border-white/10 text-[var(--cream-soft)]"} hover:border-[var(--magenta)]/60`}>
                {d === 0 ? t("innhold.off") : `${d} ${t("innhold.days_short")}`}
              </button>
            ))}
          </div>
          <div className="font-meta text-[var(--cream-faint)] mt-1">{t("innhold.young_help")}</div>
        </div>
      </Section>

      {/* SPRÅK */}
      <Section testId="languages-section">
        <SectionHeader icon={Languages} title={t("innhold.lang_title")} />
        <div className="flex gap-2 flex-wrap" data-testid="languages-options">
          {LANG_OPTS.map((l) => (
            <button key={l.v} onClick={()=>toggleLang(l.v)}
              className={`px-3 py-1.5 text-sm border ${(data.preferred_languages||[]).includes(l.v) ? "border-[var(--magenta)] text-[var(--magenta)]" : "border-white/10 text-[var(--cream-soft)]"} hover:border-[var(--magenta)]/60`}>
              {l.label}
            </button>
          ))}
        </div>
        <div className="font-meta text-[var(--cream-faint)] mt-2">{t("innhold.lang_empty")}</div>
      </Section>

      {/* ORD-FILTER */}
      <Section testId="content-words-section">
        <SectionHeader title={t("innhold.words_title")} desc={t("innhold.words_desc")} />
        <textarea
          value={dirtyWords ?? (data.blocked_words || []).join(", ")}
          onChange={(e)=>setDirtyWords(e.target.value)}
          rows={3} placeholder={t("innhold.words_ph")}
          className="input-editorial w-full max-w-md"
          data-testid="content-words-input"
        />
        <div className="font-meta text-[var(--cream-faint)] mt-1">{t("innhold.words_max")}</div>
        {dirtyWords !== null && <SaveButton onClick={saveWords} testId="save-content-words">{t("innhold.save_filter")}</SaveButton>}
      </Section>

      {/* VISNING */}
      <Section testId="view-mode-section">
        <SectionHeader title={t("innhold.view_title")} />
        <RowGroup>
          {VIEW_MODES.map((o) => (
            <RadioRow key={o.v} name="view_mode" value={o.v} current={data.view_mode}
              label={o.label} testId={`view-${o.v}`} onChange={(v)=>update({view_mode:v})} />
          ))}
        </RowGroup>
        <div className="mt-5">
          <label className="font-manrope text-sm text-[var(--cream)] block mb-2">
            {t("innhold.per_page")} <span className="font-meta text-[var(--cream-faint)]">{t("innhold.per_page_hint")}</span>
          </label>
          <div className="flex gap-2 flex-wrap" data-testid="per-page-options">
            {PER_PAGE_OPTS.map((n) => (
              <button key={n} onClick={()=>update({items_per_page:n})}
                className={`px-3 py-1 text-sm border ${data.items_per_page === n ? "border-[var(--magenta)] text-[var(--magenta)]" : "border-white/10 text-[var(--cream-soft)]"} hover:border-[var(--magenta)]/60`}>
                {n}
              </button>
            ))}
          </div>
        </div>
      </Section>
      {/* UTFORSK — opt-out */}
      <Section testId="utforsk-optout-section">
        <SectionHeader
          icon={Compass}
          title="Utforsk-feed"
          desc="Utforsk (FYP) er en vertikal strøm som viser bilder og videoer fra hele plattformen algoritmisk. Skru på hvis du IKKE ønsker at dine egne bilder skal dukke opp der."
        />
        <RowGroup>
          <ToggleRow
            testId="utforsk-hide-toggle"
            label="Skjul mine bilder og videoer fra Utforsk"
            sub="Andre steder (galleri, profil, søk) påvirkes ikke."
            checked={!!user?.utforsk_hide_from_feed}
            onChange={async (v) => {
              try {
                await api.patch("/users/me/profile", { utforsk_hide_from_feed: v });
                await refresh?.();
                toast.success(v ? "Skjult fra Utforsk" : "Vises igjen i Utforsk");
              } catch (e) { toast.error(formatApiError(e)); }
            }}
          />
        </RowGroup>
      </Section>
    </>
  );
}
